// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { forwardRef, type SelectHTMLAttributes } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SelectOption {
  value: string
  label: string
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?:    string
  error?:    string
  hint?:     string
  options:   SelectOption[]
  placeholder?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, hint, options, placeholder, className, id, ...props }, ref) => {
    const selectId = id ?? label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={selectId} className="text-sm font-medium text-ink-2">
            {label}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            aria-invalid={!!error}
            className={cn(
              'w-full appearance-none rounded-xl border bg-white px-4 py-3 pr-10',
              'text-sm text-ink',
              'transition-colors duration-150',
              'focus:outline-none focus:ring-2 focus:ring-sage-500/20 focus:border-sage-500',
              error
                ? 'border-status-alertBorder'
                : 'border-sand-300 hover:border-sage-300',
              className,
            )}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-4"
            aria-hidden
          />
        </div>

        {error && (
          <p className="text-xs text-status-alertText" role="alert">{error}</p>
        )}
        {!error && hint && (
          <p className="text-xs text-ink-4">{hint}</p>
        )}
      </div>
    )
  },
)

Select.displayName = 'Select'
